import { useState, type FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "../lib/session";
import { env } from "../lib/env";
import { isEmail } from "../lib/onboarding";
import { Logo } from "../components/Logo";
import { ConnectButton } from "../components/kit";
import { GoogleLogo } from "../components/setup/logos";
import "../components/setup/setup.css";

// Step 0.R: sign-up is invite-only (migration 20261208090000). Supabase Auth's "before user created" hook refuses an
// email that isn't on the allowlist with "Ogma is invite-only for now…"; with sign-ups switched off altogether the
// message is "Signups not allowed for this instance". Either way the person sees the invite-only note and can ask
// for access.
export function isInviteOnlyError(message: string | null | undefined): boolean {
  return /invite-only|signups? not allowed/i.test(message ?? "");
}

// A refused Google sign-in comes back to the page with the reason in the address (?error_description= or #…).
function redirectError(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return params.get("error_description") ?? hash.get("error_description");
  } catch {
    return null;
  }
}

export function SignIn() {
  const { client } = useAuth();
  const [initialError] = useState(redirectError);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(isInviteOnlyError(initialError) ? null : initialError);
  const [inviteOnly, setInviteOnly] = useState(isInviteOnlyError(initialError));
  const redirectTo = `${window.location.origin}/`;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEmail(email)) {
      setError("Enter the email address you use for work.");
      return;
    }
    setState("sending");
    setError(null);
    const { error: err } = await client.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: redirectTo } });
    if (err) {
      setState("idle");
      if (isInviteOnlyError(err.message)) setInviteOnly(true);
      else setError(/rate/i.test(err.message) ? "Too many links sent. Wait a minute, then try again." : err.message);
    } else setState("sent");
  };

  const google = async () => {
    setError(null);
    const { error: err } = await client.auth.signInWithOAuth({ provider: "google", options: { redirectTo } });
    if (err) {
      if (isInviteOnlyError(err.message)) setInviteOnly(true);
      else setError(err.message);
    }
  };

  return (
    <div className="auth">
      <Logo width={180} className="auth-logo" />
      <div className="auth-card">
        {inviteOnly ? (
          <RequestAccess client={client} initialEmail={email.trim()} onBack={() => setInviteOnly(false)} />
        ) : (
          <>
            <h1>Sign in to Ogma Hub</h1>
            {state === "sent" ? (
              <div role="status">
                <p className="lede">We've sent a sign-in link to {email.trim()}. Open it on this device to continue.</p>
                <p className="muted">No email after a minute? Check your spam folder, or <button type="button" className="link-btn" onClick={() => setState("idle")}>send another link</button>.</p>
              </div>
            ) : (
              <>
                <p className="lede">Your sales engine: what's waiting for you, your pipeline, and the assistant.</p>
                <form onSubmit={submit} noValidate>
                  <label className="field" htmlFor="email">
                    <span className="field-label">Work email</span>
                    <input id="email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? "signin-error" : undefined} />
                  </label>
                  {error && (
                    <p id="signin-error" className="form-error" role="alert">
                      {error}
                    </p>
                  )}
                  <button type="submit" className="btn btn-primary btn-block" disabled={state === "sending"}>
                    {state === "sending" ? "Sending…" : "Email me a sign-in link"}
                  </button>
                </form>
                {env.enableGoogle && (
                  <>
                    <p className="or">or</p>
                    <ConnectButton logo={<GoogleLogo />} label="Continue with Google" sub="Google Workspace or Gmail" onClick={() => void google()} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      {env.legalFooter && <p className="auth-legal muted">{env.legalFooter}</p>}
    </div>
  );
}

// The invite-only note and the "Request access" form. Records the request through crm_request_access, which a visitor
// may call with the public (anon) key; Ogma's team sees it with crm_access_requests (docs/OPERATIONS.md).
function RequestAccess({ client, initialEmail, onBack }: { client: SupabaseClient; initialEmail: string; onBack: () => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [business, setBusiness] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEmail(email)) return setError("Enter the email address you use for work.");
    if (!business.trim()) return setError("Enter your business name.");
    if (business.trim().length > 200) return setError("Keep the business name under 200 characters.");
    if (note.trim().length > 1000) return setError("Keep the note under 1,000 characters.");
    setState("sending");
    setError(null);
    const { error: err } = await client.rpc("crm_request_access", {
      p_email: email.trim(),
      p_business_name: business.trim(),
      p_note: note.trim() || null,
    });
    if (err) {
      setState("idle");
      setError(err.message || "We couldn't send your request. Try again in a minute.");
    } else setState("sent");
  };

  return (
    <>
      <h1>Ogma is invite-only for now</h1>
      {state === "sent" ? (
        <div role="status">
          <p className="lede">Thanks. We've got your request and will email {email.trim()} when your account is ready.</p>
          <p className="muted">
            Already invited? <button type="button" className="link-btn" onClick={onBack}>Back to sign in</button>
          </p>
        </div>
      ) : (
        <>
          <p className="lede">We're opening Ogma Hub to a few businesses at a time. Tell us about yours and we'll be in touch.</p>
          <form onSubmit={submit} noValidate aria-label="Request access">
            <label className="field" htmlFor="ra-email">
              <span className="field-label">Work email</span>
              <input id="ra-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="field" htmlFor="ra-business">
              <span className="field-label">Business name</span>
              <input id="ra-business" type="text" autoComplete="organization" maxLength={200} value={business} onChange={(e) => setBusiness(e.target.value)} />
            </label>
            <label className="field" htmlFor="ra-note">
              <span className="field-label">Anything we should know? (optional)</span>
              <textarea id="ra-note" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary btn-block" disabled={state === "sending"}>
              {state === "sending" ? "Sending…" : "Request access"}
            </button>
          </form>
          <p className="muted">
            <button type="button" className="link-btn" onClick={onBack}>Back to sign in</button>
          </p>
        </>
      )}
    </>
  );
}
